import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
  class: "px-4 pb-4"
};
const _hoisted_2 = {
  key: 1
};
const _hoisted_3 = {
  key: 0,
  class: "overflow-y-auto w-full"
};
const _hoisted_4 = {
  class: "relative"
};
const _hoisted_5 = ["src"];
const _hoisted_6 = {
  key: 1
};
const _hoisted_7 = {
  key: 1
};
const _hoisted_8 = {
  key: 0,
  class: "py-4"
};
const _hoisted_9 = {
  key: 1,
  class: "py-4"
};
const _hoisted_10 = {
  key: 2
};
const _hoisted_11 = {
  class: "py-6"
};
import axios from 'axios';
import { reactive, ref, onMounted, onUnmounted, computed } from 'vue';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import RouteService from '@/services/RouteService';
import { useMeStore } from '@/services/UserService/useMeStore';
import { storeToRefs } from 'pinia';

// Component Imports
import Loader from '@/components/Loader.vue';
import SnackbarComponent from '@/components/SnackbarComponent.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
export default {
  __name: 'MatterNetDocs',
  props: {
    state: Object,
    stateParams: Object,
    tracking: Object
  },
  setup(__props) {
    const props = __props;
    const {
      access
    } = storeToRefs(useMeStore());
    const {
      getMatterById
    } = useClientMatterStore();
    const {
      handleError
    } = useSnackbarHandler();
    const {
      id,
      matterId
    } = props.stateParams;
    const state = reactive({
      loading: false,
      link: undefined,
      clientAttributeExists: false,
      matterAttributeExists: false,
      validatingMatterAndClientExists: false,
      matterLoading: false,
      matter: undefined,
      isNetDocsDisconnectedUnintentionally: false
    });
    const overlayVisible = ref(true);
    const overlayDiv = ref(null);
    const routeService = new RouteService(props);
    function handleFirstInteraction() {
      props.tracking.event('Clicked', 'NetDocs Tab Interaction', {
        NetDocsTabInteraction: true
      });
      overlayVisible.value = false;
    }
    function goToNetDocsSettings() {
      props.tracking.event('Clicked', 'NetDocs Tab Settings Navigation', {
        NetDocsSettingsNavigation: true
      });
      routeService.go('settings.company.netdocs');
    }

    /* -------------------- lifecycle & api calls --------------------- */
    onMounted(async () => {
      if (!matterId && !id) {
        handleError('Client/Matter ID is required');
        return;
      }
      await checkNetDocsStatus();
      await loadLink();
      overlayDiv.value?.focus();
    });
    async function checkNetDocsStatus() {
      state.loading = true;
      try {
        const result = await axios.get('/documents/status');
        const connected = result?.data?.connected;
        const isDisconnectedUnintentionally = result?.data?.isDisconnectedUnintentionally;

        // Store the disconnection state
        state.isNetDocsDisconnectedUnintentionally = connected && isDisconnectedUnintentionally;
      } catch (error) {
        handleError(error);
      } finally {
        state.loading = false;
      }
    }
    async function loadLink() {
      if (!state.isNetDocsDisconnectedUnintentionally) {
        state.loading = true;
        try {
          const result = await axios.get(`documents/link?matterId=${matterId}`);
          // Modify the NetDocs URL to show the search bar
          let link = result.data;
          if (link && typeof link === 'string') {
            // Change smallDisplay=R to smallDisplay=Y to show the NetDocs search bar
            link = link.replace(/smallDisplay=R/g, 'smallDisplay=Y');
          }
          state.link = link;
          overlayVisible.value = true;
        } catch {
          state.link = undefined;
        } finally {
          if (!state.link) await Promise.all([validateAttributes(), loadMatter()]);
          state.loading = false;
        }
      }
    }
    async function validateAttributes() {
      state.validatingMatterAndClientExists = true;
      try {
        const [c, m] = await Promise.all([axios.get(`documents/clients/${id}/attribute-exists`), axios.get(`documents/matters/${matterId}/attribute-exists`)]);
        state.clientAttributeExists = c?.data?.attributeExists;
        state.matterAttributeExists = m?.data?.attributeExists;
      } catch (error) {
        // this is intentional to not show the error to the user
        // since we are already showing the error in the UI
        // handleError(error);
        console.error('Error validating matter and client existence:', error);
      } finally {
        state.validatingMatterAndClientExists = false;
      }
    }
    async function loadMatter() {
      state.matterLoading = true;
      try {
        state.matter = await getMatterById(matterId, true);
      } catch (error) {
        handleError(error);
      } finally {
        state.matterLoading = false;
      }
    }
    async function create() {
      const result = await axios.post(`documents/matters?matterId=${matterId}`);
      if (result.status === 200) {
        loadLink();
      }
    }
    const getRefOrMissing = item => item?.reference ?? 'Missing';
    const computedStatus = computed(() => {
      const m = state.matter;
      const hasClientId = !!m?.client?.reference;
      const hasMatterId = !!m?.reference;

      /* -------------- scenario 2: missing ID ----------------------- */
      if (!hasClientId || !hasMatterId) {
        return {
          textBeforeList: 'We can’t look for a match in NetDocuments because one or more required IDs are missing:',
          listItems: [`Client ID: ${getRefOrMissing(m?.client)}`, `Matter ID: ${getRefOrMissing(m)}`],
          textAfterList: 'Please add both the client and matter IDs in LeanLaw to check for a match or allow workspace creation in NetDocuments.',
          isButtonDisabled: true
        };
      }

      /* -------------- scenario 1 ----------------------------------- */
      if (!state.clientAttributeExists && !state.matterAttributeExists) {
        return {
          textBeforeList: 'We couldn’t find a matching client or matter in NetDocuments for:',
          listItems: [`Client ID: ${getRefOrMissing(m.client)}`, `Matter ID: ${getRefOrMissing(m)}`],
          textAfterList: `If the workspace already exists in NetDocuments, please double-check these IDs in LeanLaw to ensure they match the corresponding client and matter keys in NetDocuments. Otherwise, you can create a new workspace under the NetDocuments client ${getRefOrMissing(m.client)} using the button below.`,
          isButtonDisabled: false
        };
      }

      /* -------------- scenario 3 ----------------------------------- */
      if (state.clientAttributeExists && !state.matterAttributeExists) {
        return {
          textBeforeList: `We found a match for the client ${getRefOrMissing(m.client)} in NetDocuments, but no corresponding matter with ID ${getRefOrMissing(m)}.`,
          listItems: [`Matter ID: ${getRefOrMissing(m)}`],
          textAfterList: 'If the matter already exists in NetDocuments, please confirm that the Matter ID in LeanLaw matches the matter key in NetDocuments. Otherwise, create a new matter under the existing client using the button below.',
          isButtonDisabled: false
        };
      }

      /* -------------- scenario 4 ----------------------------------- */
      return {
        textBeforeList: 'We didn’t find a matching client or matter in NetDocuments for:',
        listItems: [`Client ID: ${getRefOrMissing(m.client)}`, `Matter ID: ${getRefOrMissing(m)}`],
        textAfterList: 'If the IDs are correct, double‑check them in LeanLaw. If not, we can create both for you using the button below.',
        isButtonDisabled: false
      };
    });
    onUnmounted(() => overlayVisible.value = true);
    return (_ctx, _cache) => {
      const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createVNode(SnackbarComponent, {
        ref: "snackbar"
      }, null, 512), state.loading ? (_openBlock(), _createBlock(Loader, {
        key: 0,
        what: "NetDocuments View",
        class: "mt-32"
      })) : (_openBlock(), _createElementBlock("div", _hoisted_2, [state.link ? (_openBlock(), _createElementBlock("div", _hoisted_3, [_createElementVNode("div", _hoisted_4, [_createElementVNode("iframe", {
        id: "NetDocuments-iframe",
        "data-cy": "NetDocuments-iframe",
        src: state.link,
        class: "w-full h-screen",
        frameborder: "0"
      }, null, 8, _hoisted_5), overlayVisible.value ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "overlayDiv",
        ref: overlayDiv,
        class: "absolute inset-0 z-10",
        style: {
          "background": "transparent"
        },
        tabindex: "0",
        onClick: handleFirstInteraction,
        onKeydown: handleFirstInteraction
      }, null, 544)) : _createCommentVNode("", true)])])) : (_openBlock(), _createElementBlock("div", _hoisted_6, [state.validatingMatterAndClientExists || state.matterLoading ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
        key: 0,
        type: "card,card"
      })) : (_openBlock(), _createElementBlock("div", _hoisted_7, [state.isNetDocsDisconnectedUnintentionally && _unref(access)?.setup ? (_openBlock(), _createElementBlock("div", _hoisted_8, [_cache[3] || (_cache[3] = _createElementVNode("p", {
        class: "text-lg mb-4"
      }, "NetDocuments and LeanLaw are disconnected.", -1)), _cache[4] || (_cache[4] = _createElementVNode("p", {
        class: "mb-6"
      }, "Go to Settings to reconnect.", -1)), _createVNode(LlSecondaryButton, {
        onBtnClick: _cache[0] || (_cache[0] = $event => goToNetDocsSettings()),
        color: "primary",
        variant: "flat"
      }, {
        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [_createTextVNode(" Settings ", -1)]))]),
        _: 1
      })])) : state.isNetDocsDisconnectedUnintentionally ? (_openBlock(), _createElementBlock("div", _hoisted_9, [...(_cache[5] || (_cache[5] = [_createElementVNode("p", {
        class: "text-lg mb-4"
      }, "NetDocuments and LeanLaw are disconnected.", -1), _createElementVNode("p", {
        class: "mb-6"
      }, "Please contact someone in your firm who has access to NetDocuments settings to reconnect.", -1)]))])) : computedStatus.value ? (_openBlock(), _createElementBlock("div", _hoisted_10, [_createElementVNode("div", null, [_createElementVNode("p", null, _toDisplayString(computedStatus.value.textBeforeList), 1), _createElementVNode("ul", null, [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(computedStatus.value.listItems, (item, index) => {
        return _openBlock(), _createElementBlock("li", {
          key: index
        }, _toDisplayString(item), 1);
      }), 128))]), _createElementVNode("p", null, _toDisplayString(computedStatus.value.textAfterList), 1)]), _createElementVNode("div", _hoisted_11, [_createVNode(LlSecondaryButton, {
        onBtnClick: _cache[1] || (_cache[1] = $event => create()),
        disabled: computedStatus.value.isButtonDisabled,
        color: "primary",
        variant: "flat"
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [_createTextVNode(" Create in NetDocuments ", -1)]))]),
        _: 1
      }, 8, ["disabled"])])])) : _createCommentVNode("", true)]))]))]))]);
    };
  }
};